import {
  FLOW_TOKEN_COOKIE_PREFIX,
  SUBMISSION_TOKEN_COOKIE_NAME,
} from './constants';

interface DetectSigningFlowInput {
  pathname: string;
  search: string;
  cookie: string;
}

const SIGNING_PATH_PREFIX = '/deals/';

/**
 * Returns true when the current pageview is part of a signing flow.
 *
 * Signal sources:
 *  - Cookie-based journey state (`__ftm_*` marker or `__submission_token`) —
 *    matches regardless of path so auth detours (e.g. `/login`,
 *    `/auth/callback/clerk`) hit during signing are still tagged.
 *  - Token query param (`flowToken` or `token`) — only honored on `/deals/*`
 *    because `token` is a generic param name that can collide with unrelated
 *    features outside the deals namespace.
 */
export function detectSigningFlowFromUrl({
  pathname,
  search,
  cookie,
}: DetectSigningFlowInput): boolean {
  // Cookie-based journey state is the primary signal and is path-agnostic.
  const hasMarkerCookie = cookie.includes(FLOW_TOKEN_COOKIE_PREFIX);
  const hasSubmissionTokenCookie = cookie.includes(
    `${SUBMISSION_TOKEN_COOKIE_NAME}=`
  );
  if (hasMarkerCookie || hasSubmissionTokenCookie) return true;

  // Token-in-URL is only trusted under `/deals/*` to avoid cross-feature
  // collisions on the generic `token` param name.
  if (!pathname.startsWith(SIGNING_PATH_PREFIX)) return false;

  // Normalize search to handle both "?foo=1" and "foo=1"
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  return params.has('flowToken') || params.has('token');
}
